import { type MetricField, type UserDimension } from "../../gql-generated/types";

export enum ComputedMetricField {
    /**
     * LENS_CTR
     *  lens_ctr(T) = swipe_ups(T) / lens_plays(T), where T is a time range
     */
    LensCTR = "LENS_CTR",

    /**
     * AVERAGE_LENS_FACE_FOUND
     *  average_lens_face_found(T) = lens_face_found(T) / lens_plays(T), where T is a time range
     */
    AverageLensFaceFound = "AVERAGE_LENS_FACE_FOUND",

    /**
     * AVERAGE_LENS_FACE_LOST
     *  average_lens_face_lost(T) = lens_face_lost(T) / lens_plays(T), where T is a time range
     */
    AverageLensFaceLost = "AVERAGE_LENS_FACE_LOST",

    /**
     * AVERAGE_LENS_OBJECT_TRACKED
     *  average_lens_object_tracked(T) = lens_object_tracked(T) / lens_plays(T), where T is a time range
     */
    AverageLensObjectTracked = "AVERAGE_LENS_OBJECT_TRACKED",

    /**
     * AVERAGE_LENS_OBJECT_TRACKED_3D
     *  average_lens_object_tracked_3d(T) = lens_object_tracked_3d(T) / lens_plays(T), where T is a time range
     */
    AverageLensObjectTracked_3D = "AVERAGE_LENS_OBJECT_TRACKED_3D",

    /**
     * AVERAGE_LENS_MARKER_TRACKED
     *  average_lens_marker_tracked(T) = lens_marker_tracked(T) / lens_plays(T), where T is a time range
     */
    AverageLensMarkerTracked = "AVERAGE_LENS_MARKER_TRACKED",

    /**
     * AVERAGE_LENS_TAP
     *  average_lens_tap(T) = lens_tap(T) / lens_plays(T), where T is a time range
     */
    AverageLensTap = "AVERAGE_LENS_TAP",
}

export type MetricFieldsUnion = MetricField | ComputedMetricField;

export interface InsightsMetrics<TStats> {
    stats: readonly TStats[];
    totalValue: number;
    totalValueFormatted: string;
    diffValue: string;
    averageValue: number;
    valueOverTime?: number;
    valueOverTimeFormatted?: string;
}

export type InsightsMetricsMap<TStats> = {
    [key in MetricFieldsUnion | UserDimension]?: InsightsMetrics<TStats>;
};

export const createDefaultInsightsMetrics = <TStats>(field?: MetricFieldsUnion): InsightsMetrics<TStats> => {
    const defaultMetric: Readonly<InsightsMetrics<TStats>> = {
        stats: [],
        totalValue: 0,
        totalValueFormatted: "-",
        diffValue: "-",
        averageValue: 0,
    };

    switch (field) {
        case ComputedMetricField.LensCTR:
            return {
                ...defaultMetric,
                valueOverTime: 0, // 𝛴 (swipe_ups) / 𝛴 (lens_plays), not equal to lens_ctr(t₁) + lens_ctr(t₂) + ...
                valueOverTimeFormatted: "-",
            };
        case ComputedMetricField.AverageLensFaceFound:
            return {
                ...defaultMetric,
                valueOverTime: 0, // 𝛴 (lens_face_found) / 𝛴 (lens_plays), not equal to average_lens_face_found(t₁) + average_lens_face_found(t₂) + ...
                valueOverTimeFormatted: "-",
            };
        case ComputedMetricField.AverageLensFaceLost:
            return {
                ...defaultMetric,
                valueOverTime: 0, // 𝛴 (lens_face_lost) / 𝛴 (lens_plays), not equal to average_lens_face_lost(t₁) + average_lens_face_lost(t₂) + ...
                valueOverTimeFormatted: "-",
            };
        case ComputedMetricField.AverageLensObjectTracked:
            return {
                ...defaultMetric,
                valueOverTime: 0, // 𝛴 (lens_object_tracked) / 𝛴 (lens_plays), not equal to average_lens_object_tracked(t₁) + average_lens_object_tracked(t₂) + ...
                valueOverTimeFormatted: "-",
            };
        case ComputedMetricField.AverageLensObjectTracked_3D:
            return {
                ...defaultMetric,
                valueOverTime: 0, // 𝛴 (lens_object_tracked_3d) / 𝛴 (lens_plays), not equal to average_lens_object_tracked_3d(t₁) + average_lens_object_tracked_3d(t₂) + ...
                valueOverTimeFormatted: "-",
            };
        case ComputedMetricField.AverageLensMarkerTracked:
            return {
                ...defaultMetric,
                valueOverTime: 0, // 𝛴 (lens_marker_tracked) / 𝛴 (lens_plays), not equal to average_lens_marker_tracked(t₁) + average_lens_marker_tracked(t₂) + ...
                valueOverTimeFormatted: "-",
            };
        case ComputedMetricField.AverageLensTap:
            return {
                ...defaultMetric,
                valueOverTime: 0, // 𝛴 (lens_tap) / 𝛴 (lens_plays), not equal to average_lens_tap(t₁) + average_lens_tap(t₂) + ...
                valueOverTimeFormatted: "-",
            };
        default:
            return defaultMetric;
    }
};
